import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'zh' | 'en';
export type Theme = 'light' | 'dark';

interface AppContextType {
  language: Language;
  theme: Theme;
  toggleLanguage: () => void;
  toggleTheme: () => void;
}

interface AppProviderProps {
  children: ReactNode;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// localStorage 的鍵值
const STORAGE_KEYS = {
  LANGUAGE: 'hot-update-language',
  THEME: 'hot-update-theme',
  INITIALIZED: 'hot-update-initialized'
} as const;

// 舊的 localStorage 鍵值（需要清理）
const OLD_STORAGE_KEYS = ['language', 'theme'];

// 預設配置
const DEFAULT_CONFIG = {
  language: 'en' as Language,
  theme: 'dark' as Theme
} as const;

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// 清理舊的 localStorage 鍵值
const cleanupOldStorage = () => {
  try {
    OLD_STORAGE_KEYS.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🧹 已清理舊的 localStorage 鍵值: ${key}`);
      }
    });
  } catch (error) {
    console.warn('⚠️ 清理舊 localStorage 時發生錯誤：', error);
  }
};

// 從 localStorage 獲取配置
const getStoredConfig = () => {
  try {
    // 先清理舊的 localStorage 鍵值
    cleanupOldStorage();
    
    const isInitialized = localStorage.getItem(STORAGE_KEYS.INITIALIZED);
    
    if (!isInitialized) {
      // 首次訪問，使用預設配置並保存到 localStorage
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, DEFAULT_CONFIG.language);
      localStorage.setItem(STORAGE_KEYS.THEME, DEFAULT_CONFIG.theme);
      localStorage.setItem(STORAGE_KEYS.INITIALIZED, 'true');
      
      console.log('🎉 首次訪問，已設置預設配置：中文 + 深色模式');
      
      return DEFAULT_CONFIG;
    } else {
      // 讀取已保存的配置
      const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE) as Language;
      const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME) as Theme;
      
      const config = {
        language: savedLanguage || DEFAULT_CONFIG.language,
        theme: savedTheme || DEFAULT_CONFIG.theme
      };
      
      console.log('📱 讀取已保存的配置：', config);
      
      return config;
    }
  } catch (error) {
    console.warn('⚠️ localStorage 讀取失敗，使用預設配置：', error);
    return DEFAULT_CONFIG;
  }
};

// 保存配置到 localStorage
const saveConfig = (language: Language, theme: Theme) => {
  try {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
    localStorage.setItem(STORAGE_KEYS.INITIALIZED, 'true');
    
    console.log('💾 配置已保存到 localStorage：', { language, theme });
  } catch (error) {
    console.warn('⚠️ localStorage 保存失敗：', error);
  }
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // 初始化時從 localStorage 讀取配置
  const [config, setConfig] = useState(() => getStoredConfig());
  const [language, setLanguage] = useState<Language>(config.language);
  const [theme, setTheme] = useState<Theme>(config.theme);

  const toggleLanguage = () => {
    const newLanguage = language === 'zh' ? 'en' : 'zh';
    setLanguage(newLanguage);
    
    // 立即保存到 localStorage
    saveConfig(newLanguage, theme);
    
    console.log(`🌐 語言已切換：${language} → ${newLanguage}`);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // 立即保存到 localStorage
    saveConfig(language, newTheme);
    
    console.log(`🌓 主題已切換：${theme} → ${newTheme}`);
  };

  // 當主題變更時，更新 DOM class
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // 組件掛載時確保 DOM 狀態正確
  useEffect(() => {
    // 確保初始化時 DOM 狀態正確
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    console.log('✅ AppProvider 初始化完成，當前配置：', { language, theme });
  }, []);

  const value = {
    language,
    theme,
    toggleLanguage,
    toggleTheme,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}; 